<template>
  <div class="season-list-wrap">
    <h2 class="section-title">Seasons</h2>

    <div v-if="loading" class="loading-spinner">
      <p>Loading seasons...</p>
    </div>

    <div v-else-if="seasons.length === 0" class="no-seasons">
      <p>No seasons available at the moment.</p>
    </div>

    <div v-else class="row">
      <div
        v-for="season in seasons"
        :key="season.season_id"
        class="col-md-4 col-sm-6 mb-4"
      >
        <div class="season-card">
          <div class="season-image">
            <img
              :src="season.banner_image || defaultImage"
              :alt="season.title"
              @error="handleImageError"
            />
            <!-- Gender Badge -->
            <span
              class="gender-badge"
              :class="getGenderBadgeClass(season.gender)"
            >
              {{ getGenderLabel(season.gender) }}
            </span>
          </div>
          <div class="season-content">
            <h3>{{ season.title }}</h3>
            <div class="season-dates">
              <p class="season-period">
                <i class="fa fa-calendar"></i>
                {{ formatDate(season.season_start) }} -
                {{ formatDate(season.season_end) }}
              </p>
              <p class="application-period">
                <i class="fa fa-clock"></i>
                Registration: {{ formatDate(season.registration_start) }} -
                {{ formatDate(season.registration_end) }}
              </p>
            </div>
            <p class="season-description">{{ season.summary }}</p>

            <!-- Gender Restriction Warning -->
            <div
              v-if="season.gender_restriction_message"
              class="alert alert-warning"
            >
              <i class="fa fa-exclamation-triangle"></i>
              {{ season.gender_restriction_message }}
            </div>

            <div class="season-actions">
              <button
                v-if="season.user_application_status === 'available'"
                class="btn btn-universal participate-btn"
                @click="participateInSeason(season)"
                :disabled="!season.can_register || isParticipating"
              >
                <template v-if="isParticipating"> Processing... </template>
                <template v-else-if="season.can_register"> Apply Now </template>
                <template v-else>
                  {{ getDisabledReason(season) }}
                </template>
              </button>

              <button
                v-else-if="season.user_application_status === 'under_review'"
                class="btn btn-warning"
                disabled
              >
                <i class="fa fa-clock"></i> Under Review
              </button>

              <button
                v-else-if="season.user_application_status === 'accepted'"
                class="btn btn-success"
                disabled
              >
                <i class="fa fa-check"></i> Accepted
              </button>

              <button
                v-else-if="season.user_application_status === 'declined'"
                class="btn btn-danger"
                disabled
              >
                <i class="fa fa-times"></i> Declined
              </button>

              <button
                v-else-if="season.user_application_status === 'upcoming'"
                class="btn btn-secondary"
                disabled
              >
                <i class="fa fa-calendar"></i> Opens Soon
              </button>

              <button v-else class="btn btn-secondary" disabled>
                <i class="fa fa-lock"></i> Registration Closed
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="pagination.lastPage > 1" class="pagination-wrap">
      <nav>
        <ul class="pagination">
          <li
            class="page-item"
            :class="{ disabled: pagination.currentPage === 1 }"
          >
            <a
              class="page-link"
              href="#"
              @click.prevent="changePage(pagination.currentPage - 1)"
            >
              Previous
            </a>
          </li>

          <li
            v-for="page in displayPages"
            :key="page"
            class="page-item"
            :class="{ active: page === pagination.currentPage }"
          >
            <a class="page-link" href="#" @click.prevent="changePage(page)">
              {{ page }}
            </a>
          </li>

          <li
            class="page-item"
            :class="{
              disabled: pagination.currentPage === pagination.lastPage,
            }"
          >
            <a
              class="page-link"
              href="#"
              @click.prevent="changePage(pagination.currentPage + 1)"
            >
              Next
            </a>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script>
export default {
  name: "SeasonList",

  props: {
    seasons: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isParticipating: false,
      defaultImage: "/assets/images/no-image.png",
    };
  },

  computed: {
    displayPages() {
      const current = this.pagination.currentPage;
      const last = this.pagination.lastPage;
      const pages = [];

      let start = Math.max(1, current - 2);
      let end = Math.min(last, current + 2);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    },
  },

  methods: {
    handleImageError(event) {
      // Prevent infinite loop if default image also fails
      if (event.target.src !== this.defaultImage) {
        event.target.src = this.defaultImage;
      }
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.lastPage) return;
      this.$emit("page-changed", page);
    },

    formatDate(unixTimestamp) {
      return new Date(unixTimestamp * 1000).toLocaleDateString();
    },

    getGenderLabel(gender) {
      const labels = {
        male: "Male Only",
        female: "Female Only",
      };
      return labels[gender] || gender;
    },

    getGenderBadgeClass(gender) {
      return {
        "badge-male": gender === "male",
        "badge-female": gender === "female",
      };
    },

    getDisabledReason(season) {
      if (season.gender_restriction_message) {
        return "Not Eligible";
      }
      return "Cannot Register";
    },

    async participateInSeason(season) {
      // Check eligibility before attempting to participate
      if (!season.can_register) {
        if (season.gender_restriction_message) {
          global.app.$toastr.error(season.gender_restriction_message);
        } else {
          global.app.$toastr.error("You cannot register for this season");
        }
        return;
      }

      this.isParticipating = true;
      try {
        const result = await this.$store.dispatch(
          "season/participateInSeason",
          season.season_id
        );
        if (result.success) {
          global.app.$toastr.success(result.message);
        } else {
          global.app.$toastr.error(result.message);
        }
      } catch (error) {
        global.app.$toastr.error(
          "An error occurred while applying for the season"
        );
      } finally {
        this.isParticipating = false;
      }
    },
  },
};
</script>

<style scoped>
.section-title {
  font-size: 2rem;
  font-weight: 700;
  color: #212529;
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 3px solid #ffc107;
  display: inline-block;
}

.season-card {
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.3s, box-shadow 0.3s;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.season-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.season-image {
  position: relative;
}

.season-image img {
  width: 100%;
  height: 200px;
  object-fit: cover;
}

.gender-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 8px 16px; /* Increased from 6px 12px */
  border-radius: 20px;
  font-size: 0.85rem; /* Increased from 0.75rem */
  font-weight: 700; /* Increased from 600 */
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3); /* Stronger shadow */
}
.badge-male {
  background: #ffc107; /* Changed to match theme */
  color: #212529; /* Dark text */
  border: 2px solid #e0a800;
}

.badge-female {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.season-content {
  padding: 20px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.season-content h3 {
  margin-bottom: 15px;
  font-size: 1.5rem;
  color: #333;
}

.season-dates {
  margin-bottom: 15px;
}

.season-period,
.application-period {
  margin-bottom: 8px;
  color: #666;
  font-size: 0.9rem;
}

.season-period i,
.application-period i {
  margin-right: 5px;
  color: #666;
}

.season-description {
  color: #666;
  margin-bottom: 15px;
  flex-grow: 1;
  font-size: 0.9rem;
  line-height: 1.4;
}

.alert {
  padding: 10px 12px;
  border-radius: 5px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
}

.alert-warning {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.alert-warning i {
  color: #856404;
}

.season-actions {
  margin-top: auto;
}

.participate-btn {
  width: 100%;
}

.btn {
  width: 100%;
  padding: 12px 16px; /* Increased from 10px */
  font-weight: 600; /* Increased from 500 */
  font-size: 1rem; /* Added - explicit font size */
  border-radius: 5px;
  transition: all 0.3s;
}

.btn i {
  margin-right: 5px;
}

.btn-universal,
.btn-primary {
  background-color: #ffc107; /* Changed to yellow theme */
  border-color: #ffc107;
  color: #212529; /* Dark text for yellow background */
  font-size: 1.1rem; /* Larger text for primary action */
}

.btn-universal:hover:not(:disabled),
.btn-primary:hover:not(:disabled) {
  background-color: #e0a800; /* Darker yellow on hover */
  border-color: #d39e00;
  color: #212529;
}

.btn-universal:disabled,
.btn-primary:disabled {
  background-color: #6c757d;
  border-color: #6c757d;
  cursor: not-allowed;
  opacity: 0.65;
  color: white;
}

.btn-warning {
  background-color: #ffc107;
  border-color: #ffc107;
  color: #212529;
  font-size: 1rem;
}

.btn-success {
  background-color: #28a745;
  border-color: #28a745;
  font-size: 1rem;
}

.btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
  font-size: 1rem;
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
  font-size: 1rem;
}

.participation-info {
  border-top: 1px solid #eee;
  padding-top: 10px;
  margin-top: 10px;
}

.loading-spinner {
  text-align: center;
  padding: 40px;
}

.no-seasons {
  text-align: center;
  padding: 40px;
  color: #666;
}

.pagination-wrap {
  margin-top: 30px;
  display: flex;
  justify-content: center;
}

.pagination {
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0;
  gap: 8px;
}

.pagination .page-item {
  margin: 0;
}

.pagination .page-link {
  display: block;
  padding: 10px 16px;
  background-color: #fff;
  border: 2px solid #dee2e6;
  color: #212529;
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.3s;
  font-weight: 500;
  font-size: 1rem;
}

.pagination .page-link:hover {
  background-color: #fff3cd;
  border-color: #ffc107;
  color: #212529;
}

.pagination .page-item.active .page-link {
  background-color: #ffc107;
  border-color: #ffc107;
  color: #212529;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(255, 193, 7, 0.4);
}

.pagination .page-item.disabled .page-link {
  color: #6c757d;
  pointer-events: none;
  background-color: #f8f9fa;
  border-color: #dee2e6;
  opacity: 0.5;
}
.pagination .page-link[rel="prev"],
.pagination .page-link[rel="next"] {
  font-weight: 600;
}

@media (max-width: 768px) {
  .season-content h3 {
    font-size: 1.3rem;
  }

  .season-dates {
    font-size: 0.8rem;
  }

  .season-description {
    font-size: 0.85rem;
  }

  .gender-badge {
    font-size: 0.7rem;
    padding: 4px 8px;
  }

  .alert {
    font-size: 0.8rem;
    padding: 8px 10px;
  }
  .pagination .page-link {
    padding: 8px 12px;
    font-size: 0.9rem;
  }
}
</style>
